"use client";

import { CheckIcon } from "@heroicons/react/20/solid";

import { Button } from "@/components/buttons/Button";
import { Heading4 } from "@/components/text/Heading4";
import { NormalText } from "@/components/text/NormalText";
import { useSetAuthModalOpen } from "@/lib/clerk/useSetAuthModalOpen";
import { usePricingModal } from "@/lib/zustand/usePricingModal";
import { useNamedSelectedArtist } from "@/lib/zustand/useSelectedArtist";
import {
  MixedModelConstructedNoDates,
  ModelNoDates,
  UserNoDates,
} from "@/types/types";
import { getArtistInstrumentModelMixedModel } from "@/utils/artistInstrumentModelMixedModel/get/getArtistInstrumentModelMixedModel";

const upgradeBenefits = [
  "More conversion time",
  "The ability to create custom voices",
  "Additional mixed voices slots",
  "Commercial use of your conversions",
  "WAV (24-bit) file downloads",
];

export function LockedArtist({
  user,
  models,
  namespace,
  mixedModelsConstructed,
}: {
  namespace: string;
  user: UserNoDates | null;
  models: ModelNoDates[] | null;
  mixedModelsConstructed: MixedModelConstructedNoDates[] | null;
}) {
  const setPricingModalOpen = usePricingModal((state) => state.setOpen);
  const setAuthModalOpen = useSetAuthModalOpen();

  const useSelectedArtist = useNamedSelectedArtist((state) =>
    state.getNamedStore(namespace)
  );

  const selectedArtistId = useSelectedArtist((state) => state.selectedArtistId);

  const artistInstrumentModelMixedModel = getArtistInstrumentModelMixedModel(
    selectedArtistId,
    models,
    mixedModelsConstructed
  );

  const name =
    (
      artistInstrumentModelMixedModel !== null &&
      "name" in artistInstrumentModelMixedModel
    ) ?
      artistInstrumentModelMixedModel.name
    : " the voice";

  return (
    <div className="mt-4 rounded-3xl bg-primaryLight p-4">
      <Heading4 elementType="h3" className="mb-2 text-center">
        {name} locked
      </Heading4>

      <NormalText elementType="p">
        Upgrade to a paid plan to unlock {name} along with a variety of other
        royalty-free voices. In addition to this, you will also get:
      </NormalText>

      <ul className="mt-4 space-y-3">
        {upgradeBenefits.map((benefit) => (
          <li key={benefit} className="flex gap-x-2">
            <CheckIcon
              className="h-5 w-5 flex-none text-primary"
              aria-hidden="true"
            />
            <NormalText elementType="span" className="text-sm font-medium">
              {benefit}
            </NormalText>
          </li>
        ))}
      </ul>
      <Button
        variant="primary"
        onClick={() => {
          if (user === null) {
            setAuthModalOpen();
            return;
          }
          setPricingModalOpen();
        }}
        className="mx-auto mt-6 block"
      >
        Upgrade to unlock {name}
      </Button>
    </div>
  );
}
